import { useCallback, useEffect, useState } from 'react'

import { Box, Typography, useTheme } from '@mui/material'
import { ClockCounterClockwise, FolderOpen, User } from '@phosphor-icons/react'
import { FormattedMessage } from 'react-intl'

import { ToggleButton } from '@acre/design-system'

export type GlobalSearchType = 'recents' | 'clients' | 'cases'

type Props = {
  selectedType?: GlobalSearchType
  onSelectionChange: (selected: GlobalSearchType) => void
}

const GlobalSearchToggleButtonGroup = ({ selectedType = 'recents', onSelectionChange }: Props) => {
  const theme = useTheme()

  const [selection, setSelection] = useState<GlobalSearchType>(selectedType)

  useEffect(() => {
    if (selectedType !== selection) {
      setSelection(selectedType)
    }
  }, [selectedType, selection])

  const handleClick = useCallback(
    (type: GlobalSearchType) => {
      setSelection(type)
      onSelectionChange(type)
    },
    [onSelectionChange],
  )

  return (
    <Box display="flex" gap={0.5} flexDirection="row">
      <ToggleButton
        selected={selection === 'recents'}
        value={1}
        variant="outlined"
        onClick={() => handleClick('recents')}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            lineHeight={1}
            fontSize={theme.typography.pxToRem(12)}
            fontWeight={theme.typography.fontWeightBold}
          >
            <FormattedMessage id="nav.search.recents" />
          </Typography>
          <ClockCounterClockwise size={16} weight="bold" />
        </Box>
      </ToggleButton>
      <ToggleButton
        selected={selection === 'clients'}
        value={2}
        variant="outlined"
        onClick={() => handleClick('clients')}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            lineHeight={1}
            fontSize={theme.typography.pxToRem(12)}
            fontWeight={theme.typography.fontWeightBold}
          >
            <FormattedMessage id="nav.search.clients" />
          </Typography>
          <User size={16} weight="bold" />
        </Box>
      </ToggleButton>
      <ToggleButton selected={selection === 'cases'} value={3} variant="outlined" onClick={() => handleClick('cases')}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            lineHeight={1}
            fontSize={theme.typography.pxToRem(12)}
            fontWeight={theme.typography.fontWeightBold}
          >
            <FormattedMessage id="nav.search.cases" />
          </Typography>
          <FolderOpen size={16} weight="bold" />
        </Box>
      </ToggleButton>
    </Box>
  )
}

export default GlobalSearchToggleButtonGroup
